import React, { useEffect, useState } from "react";
import CrudPage from "@/components/common/CrudPage";
import { useApp } from "@/contexts/AppContext";
import { api, formatError } from "@/lib/api";
import { BookMarked, Sparkles, Plus } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { k: "title", label: "Τίτλος / Αναφορά" },
  { k: "court", label: "Δικαστήριο" },
  { k: "citation", label: "Παραπομπή (citation)" },
  { k: "principle", label: "Νομική αρχή", type: "textarea", rows: 3 },
  { k: "relevance", label: "Σχετικότητα με την υπόθεση", type: "textarea", rows: 3 },
  { k: "side", label: "Υπέρ", type: "select", options: [
    { value: "defense", label: "Υπεράσπιση" },
    { value: "prosecution", label: "Κατηγορία" },
    { value: "neutral", label: "Ουδέτερο" },
  ]},
  { k: "key_quote", label: "Κεντρικό απόσπασμα", type: "textarea", rows: 3 },
  { k: "notes", label: "Σημειώσεις", type: "textarea", rows: 2 },
  { k: "tags", label: "Ετικέτες (διαχωρισμός κόμμα)" },
];

function SolonSuggest({ onAdded }) {
  const { currentCase } = useApp();
  const [available, setAvailable] = useState(null);
  // Ποια μηχανή απαντά· η ετικέτα δεν πρέπει να λέει «Solon» όταν μιλά το Themis.
  const [engine, setEngine] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);

  useEffect(() => {
    api.get("/integrations/status").then(r => {
      setAvailable(!!r.data?.caselaw);
      setEngine(r.data?.themis ? "Themis" : r.data?.solon ? "Solon" : "");
    }).catch(() => setAvailable(false));
  }, []);

  const suggest = async () => {
    if (!currentCase) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/cases/${currentCase.id}/case-law/suggest`, { q: q || null });
      setResults(data.results || []);
      if (!(data.results || []).length) toast.message(`Το ${engine || "σύστημα"} δεν επέστρεψε αποτελέσματα για αυτό το ερώτημα`);
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const add = async (r) => {
    try {
      await api.post(`/cases/${currentCase.id}/case-law`, {
        title: r.title, court: r.court, citation: r.citation,
        principle: r.principle, side: "neutral",
        notes: r.url ? `Πηγή: ${r.url}` : `Πηγή: ${r.source || "solon"} (αυτούσιο απόσπασμα)`,
        tags: [r.source || "solon"],
      });
      toast.success(`Προστέθηκε: ${r.title}`);
      onAdded();
    } catch (e) { toast.error(formatError(e)); }
  };

  if (available === false) {
    return (
      <div className="surface-card p-4 mb-6 text-[12px] text-muted-foreground" data-testid="solon-unavailable">
        <Sparkles className="size-3.5 inline mr-1.5 gold-text"/>
        Η αυτόματη πρόταση νομολογίας δεν έχει ρυθμιστεί σε αυτή την εγκατάσταση
        (THEMIS_API_URL ή SOLON_API_URL στο .env). Η χειροκίνητη καταχώρηση λειτουργεί κανονικά.
      </div>
    );
  }

  return (
    <div className="surface-card p-5 mb-6" data-testid="solon-suggest-panel">
      <h3 className="font-heading font-semibold text-[13.5px] mb-1 flex items-center gap-2">
        <Sparkles className="size-4 gold-text"/>Προτάσεις νομολογίας{engine ? ` (${engine})` : ""}
      </h3>
      <p className="text-[11.5px] text-muted-foreground mb-3">
        Αναζήτηση στο corpus νομολογίας βάσει των κατηγοριών της υπόθεσης — ή δώστε δικό σας ερώτημα.
        Το απόσπασμα κάθε απόφασης αποθηκεύεται αυτούσιο.
      </p>
      <div className="flex gap-2 mb-3">
        <input data-testid="solon-query-input" value={q} onChange={e => setQ(e.target.value)}
          onKeyDown={e => e.key === "Enter" && suggest()}
          placeholder={`π.χ. ${(currentCase?.charges || []).join(", ") || "επικίνδυνη σωματική βλάβη άμυνα"}`}
          className="flex-1 bg-muted/30 border border-border/80 rounded-md px-3 py-2 text-[12.5px] outline-none focus:border-[hsl(var(--gold)/0.5)]"/>
        <button data-testid="solon-suggest-btn" onClick={suggest} disabled={busy || available === null}
          className="px-4 py-2 rounded-md bg-[hsl(var(--gold))] text-[hsl(var(--background))] font-medium text-[12.5px] disabled:opacity-60">
          {busy ? "…" : "Αναζήτηση"}
        </button>
      </div>
      <div className="space-y-2 max-h-[300px] overflow-y-auto scrollbar-thin">
        {results.map((r, i) => (
          <div key={i} className="p-3 rounded border border-border/50 hover:border-[hsl(var(--gold)/0.3)]" data-testid={`solon-result-${i}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[12.5px] font-medium">{r.title}</div>
                <div className="text-[10.5px] font-mono text-muted-foreground">{r.court} · {r.citation}</div>
                {r.principle && <p className="text-[12px] text-muted-foreground mt-1 leading-relaxed">{r.principle.slice(0, 240)}{r.principle.length > 240 ? "…" : ""}</p>}
              </div>
              <button onClick={() => add(r)} data-testid={`solon-add-${i}`}
                className="shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-[hsl(var(--gold)/0.4)] gold-text text-[11.5px] hover:bg-[hsl(var(--gold)/0.1)]">
                <Plus className="size-3.5"/>Προσθήκη
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CaseLaw() {
  const [refreshKey, setRefreshKey] = useState(0);
  return (
    <div>
      <SolonSuggest onAdded={() => setRefreshKey(k => k + 1)}/>
      <CrudPage
        key={refreshKey}
        slug="case-law" title="Νομολογία"
        subtitle="Βιβλιοθήκη νομικής έρευνας — αποφάσεις, αρχές, παραπομπές."
        emptyIcon={BookMarked} fields={FIELDS}
        defaultItem={{ side: "defense" }}
        listColumns={{
          title: it => it.title,
          subtitle: it => `${it.court || "—"} · ${it.citation || ""}`,
          body: it => it.principle,
        }}
        badges={{ side: { field: "side" } }}
      />
    </div>
  );
}
